import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-hot-toast';
import { format, parseISO, subMonths } from 'date-fns';
import {
  ChartBarIcon,
  DocumentChartBarIcon,
  TableCellsIcon,
  CalendarIcon,
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
  CurrencyDollarIcon,
  BanknotesIcon,
  CalculatorIcon,
  ArrowDownTrayIcon
} from '@heroicons/react/24/outline';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import DateQuickPresets from '../../components/DateQuickPresets';

const Relatorios = () => {
  const INDICADORES_DEFAULT = {
    faturamento: 0,
    despesasOperacionais: 0,
    despesasTributarias: 0,
    resultadoLiquido: 0,
    margemBruta: 0,
    receitaOperacional: 0
  };

  const toNumber = (value) => {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : 0;
  };

  const [periodo, setPeriodo] = useState({
    inicio: format(subMonths(new Date(), 6), 'yyyy-MM-dd'),
    fim: format(new Date(), 'yyyy-MM-dd'),
    regime: 'caixa'
  });
  
  const [dashboardData, setDashboardData] = useState(null);
  const [carregando, setCarregando] = useState(false);

  useEffect(() => {
    carregarRelatorios();
  }, [periodo]);

  const carregarRelatorios = async () => {
    setCarregando(true);
    try {
      const response = await axios.get('/api/dashboard', {
        params: {
          data_inicio: periodo.inicio,
          data_fim: periodo.fim,
          regime: periodo.regime,
        }
      });
      const payload = response?.data || {};
      const rawIndicadores = payload?.indicadores || {};
      const rawGraficos = payload?.graficos || {};

      const indicadores = {
        ...INDICADORES_DEFAULT,
        ...Object.fromEntries(
          Object.entries(rawIndicadores).map(([chave, valor]) => [chave, toNumber(valor)])
        )
      };

      const mensal = Array.isArray(rawGraficos.mensal)
        ? rawGraficos.mensal.map((item) => ({
            ...item,
            receitas: toNumber(item?.receitas),
            despesas: toNumber(item?.despesas)
          }))
        : [];

      const categorias = Array.isArray(rawGraficos.categorias)
        ? rawGraficos.categorias.map((item) => ({
            ...item,
            nome: item?.nome || 'Sem categoria',
            valor: toNumber(item?.valor)
          }))
        : [];

      setDashboardData({
        indicadores,
        graficos: {
          mensal,
          categorias
        }
      });
    } catch (error) {
      console.error('Erro ao carregar relatórios:', error);
      toast.error('Erro ao carregar dados dos relatórios');
    } finally {
      setCarregando(false);
    }
  };

  const exportarRelatorio = async (tipo) => {
    try {
      let url, filename;
      
      switch (tipo) {
        case 'pdf':
          url = `/api/exportar/pdf/dre?data_inicio=${periodo.inicio}&data_fim=${periodo.fim}`;
          filename = `relatorio_dre_${periodo.inicio}_${periodo.fim}.pdf`;
          break;
        case 'excel':
          url = `/api/exportar/excel/lancamentos?data_inicio=${periodo.inicio}&data_fim=${periodo.fim}`;
          filename = `lancamentos_${periodo.inicio}_${periodo.fim}.xlsx`;
          break;
        default:
          return;
      }
      
      const response = await axios.get(url, { responseType: 'blob' });
      const blob = new Blob([response.data]);
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success('Relatório exportado com sucesso!');
    } catch (error) {
      console.error('Erro ao exportar relatório:', error);
      toast.error('Erro ao exportar relatório');
    }
  };

  const COLORS = ['#10B981', '#EF4444', '#3B82F6', '#8B5CF6', '#F59E0B'];

  if (!dashboardData) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="loader h-8 w-8"></div>
        <span className="ml-3 text-gray-600">Carregando relatórios...</span>
      </div>
    );
  }

  const indicadores = dashboardData?.indicadores || INDICADORES_DEFAULT;
  const graficos = dashboardData?.graficos || { mensal: [], categorias: [] };

  return (
    <div className="animate-fade-in">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">📈 Relatórios Avançados</h1>
          <p className="text-gray-600">Análises detalhadas e gráficos personalizados</p>
        </div>
        <div className="flex flex-col items-end gap-2">
          <div className="flex items-center space-x-2">
            <CalendarIcon className="h-5 w-5 text-gray-400" />
            <input
              type="date"
              value={periodo.inicio}
              onChange={(e) => setPeriodo({ ...periodo, inicio: e.target.value })}
              className="form-input text-sm"
            />
            <span>até</span>
            <input
              type="date"
              value={periodo.fim}
              onChange={(e) => setPeriodo({ ...periodo, fim: e.target.value })}
              className="form-input text-sm"
            />
            <select
              value={periodo.regime}
              onChange={(e) => setPeriodo({ ...periodo, regime: e.target.value })}
              className="form-select text-sm"
            >
              <option value="caixa">Caixa</option>
              <option value="competencia">Competencia</option>
            </select>
          </div>
          <DateQuickPresets
            mode="range"
            startDate={periodo.inicio}
            endDate={periodo.fim}
            onSelect={({ startDate, endDate }) =>
              setPeriodo((old) => ({ ...old, inicio: startDate, fim: endDate }))
            }
          />
        </div>
      </div>

      {/* Cards de Exportação */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
        <div className="card cursor-pointer hover:shadow-md transition-shadow" onClick={() => exportarRelatorio('pdf')}>
          <div className="card-body">
            <div className="flex items-center">
              <div className="p-3 rounded-full bg-danger-100">
                <DocumentChartBarIcon className="h-6 w-6 text-danger-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Relatório DRE (PDF)</p>
                <p className="text-lg font-semibold">Exportar PDF</p>
              </div>
            </div>
          </div>
        </div>

        <div className="card cursor-pointer hover:shadow-md transition-shadow" onClick={() => exportarRelatorio('excel')}>
          <div className="card-body">
            <div className="flex items-center">
              <div className="p-3 rounded-full bg-success-100">
                <TableCellsIcon className="h-6 w-6 text-success-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Lançamentos (Excel)</p>
                <p className="text-lg font-semibold">Exportar Excel</p>
              </div>
            </div>
          </div>
        </div>

        <div className="card cursor-pointer hover:shadow-md transition-shadow" onClick={() => {
          exportarRelatorio('pdf');
          exportarRelatorio('excel');
        }}>
          <div className="card-body">
            <div className="flex items-center">
              <div className="p-3 rounded-full bg-primary-100">
                <ArrowDownTrayIcon className="h-6 w-6 text-primary-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Pacote Completo</p>
                <p className="text-lg font-semibold">Exportar Tudo</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Gráficos Principais */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        {/* Gráfico de Linha - Evolução Mensal */}
        <div className="card">
          <div className="card-header">
            <h3 className="text-lg font-semibold">📈 Evolução Mensal</h3>
          </div>
          <div className="card-body">
            <div className="h-80">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={graficos.mensal}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis 
                    dataKey="mes" 
                    stroke="#6b7280"
                    fontSize={12}
                  />
                  <YAxis 
                    stroke="#6b7280"
                    fontSize={12}
                    tickFormatter={(value) => `R$ ${(value / 1000).toFixed(0)}k`}
                  />
                  <Tooltip 
                    formatter={(value) => [`R$ ${toNumber(value).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`, 'Valor']}
                    labelFormatter={(label) => `Mês: ${label}`}
                  />
                  <Legend />
                  <Line 
                    type="monotone" 
                    dataKey="receitas" 
                    name="Receitas"
                    stroke="#10B981" 
                    strokeWidth={2}
                    dot={{ r: 4 }}
                    activeDot={{ r: 6 }}
                  />
                  <Line 
                    type="monotone" 
                    dataKey="despesas" 
                    name="Despesas"
                    stroke="#EF4444" 
                    strokeWidth={2}
                    dot={{ r: 4 }}
                    activeDot={{ r: 6 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>

        {/* Gráfico de Pizza - Composição */}
        <div className="card">
          <div className="card-header">
            <h3 className="text-lg font-semibold">🍕 Composição Financeira</h3>
          </div>
          <div className="card-body">
            <div className="h-80">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={graficos.categorias}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, value }) => `${name}: R$ ${(value / 1000).toFixed(1)}k`}
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="valor"
                  >
                    {graficos.categorias.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip 
                    formatter={(value) => [`R$ ${toNumber(value).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`, 'Valor']}
                  />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
      </div>

      {/* Análises Detalhadas */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <div className="card">
          <div className="card-body">
            <div className="flex items-center mb-4">
              <div className="p-2 rounded-lg bg-success-100">
                <ArrowTrendingUpIcon className="h-5 w-5 text-success-600" />
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-600">Receita Operacional</p>
                <p className="text-xl font-bold">
                  R$ {indicadores.receitaOperacional.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                </p>
              </div>
            </div>
            <div className="text-xs text-gray-500">
              Principal fonte de receitas da empresa
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-body">
            <div className="flex items-center mb-4">
              <div className="p-2 rounded-lg bg-danger-100">
                <ArrowTrendingDownIcon className="h-5 w-5 text-danger-600" />
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-600">Despesas Operacionais</p>
                <p className="text-xl font-bold">
                  R$ {indicadores.despesasOperacionais.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                </p>
              </div>
            </div>
            <div className="text-xs text-gray-500">
              Custos diretos da operação
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-body">
            <div className="flex items-center mb-4">
              <div className="p-2 rounded-lg bg-warning-100">
                <BanknotesIcon className="h-5 w-5 text-warning-600" />
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-600">Despesas Tributárias</p>
                <p className="text-xl font-bold">
                  R$ {indicadores.despesasTributarias.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                </p>
              </div>
            </div>
            <div className="text-xs text-gray-500">
              Impostos e tributos do período
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-body">
            <div className="flex items-center mb-4">
              <div className="p-2 rounded-lg bg-primary-100">
                <CalculatorIcon className="h-5 w-5 text-primary-600" />
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-600">Margem Líquida</p>
                <p className="text-xl font-bold">
                  {indicadores.margemBruta}%
                </p>
              </div>
            </div>
            <div className="text-xs text-gray-500">
              Lucratividade do negócio
            </div>
          </div>
        </div>
      </div>

      {/* Tabela de Análise Comparativa */}
      <div className="card mb-6">
        <div className="card-header">
          <h3 className="text-lg font-semibold">📊 Análise Comparativa por Categoria</h3>
        </div>
        <div className="card-body p-0">
          <div className="table-container">
            <table className="table">
              <thead>
                <tr>
                  <th>Categoria</th>
                  <th>Valor (R$)</th>
                  <th>Percentual</th>
                  <th>Tendência</th>
                  <th>Análise</th>
                </tr>
              </thead>
              <tbody>
                {graficos.categorias.map((categoria, index) => {
                  const percentual = indicadores.faturamento > 0
                    ? (toNumber(categoria.valor) / indicadores.faturamento) * 100
                    : 0;
                  let analise = '';
                  let tendencia = 'estavel';
                  
                  if (percentual > 40) {
                    analise = 'Alto impacto';
                    tendencia = 'alta';
                  } else if (percentual > 20) {
                    analise = 'Impacto moderado';
                    tendencia = 'moderada';
                  } else {
                    analise = 'Baixo impacto';
                    tendencia = 'baixa';
                  }
                  
                  return (
                    <tr key={index}>
                      <td>
                        <div className="flex items-center">
                          <div 
                            className="w-3 h-3 rounded-full mr-2"
                            style={{ backgroundColor: COLORS[index % COLORS.length] }}
                          ></div>
                          <span>{categoria.nome}</span>
                        </div>
                      </td>
                      <td className="font-medium">
                        R$ {toNumber(categoria.valor).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                      </td>
                      <td>
                        <div className="flex items-center">
                          <div className="w-24 bg-gray-200 rounded-full h-2 mr-2">
                            <div 
                              className="h-2 rounded-full"
                              style={{ 
                                width: `${Math.min(percentual, 100)}%`,
                                backgroundColor: COLORS[index % COLORS.length]
                              }}
                            ></div>
                          </div>
                          <span>{percentual.toFixed(1)}%</span>
                        </div>
                      </td>
                      <td>
                        <span className={`badge ${
                          tendencia === 'alta' ? 'badge-danger' :
                          tendencia === 'moderada' ? 'badge-warning' :
                          'badge-success'
                        }`}>
                          {tendencia === 'alta' ? 'Alta' :
                           tendencia === 'moderada' ? 'Moderada' : 'Baixa'}
                        </span>
                      </td>
                      <td className="text-sm text-gray-600">
                        {analise}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Recomendações Baseadas em Dados */}
      <div className="card">
        <div className="card-header">
          <h3 className="text-lg font-semibold">💡 Recomendações Estratégicas</h3>
        </div>
        <div className="card-body">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h4 className="font-semibold text-gray-800 mb-3">📈 Oportunidades</h4>
              <ul className="space-y-2">
                <li className="flex items-start">
                  <div className="w-2 h-2 rounded-full bg-success-500 mt-1.5 mr-2 flex-shrink-0"></div>
                  <span>Receita operacional apresenta crescimento consistente</span>
                </li>
                <li className="flex items-start">
                  <div className="w-2 h-2 rounded-full bg-success-500 mt-1.5 mr-2 flex-shrink-0"></div>
                  <span>Margem de lucro dentro dos padrões do setor</span>
                </li>
                <li className="flex items-start">
                  <div className="w-2 h-2 rounded-full bg-success-500 mt-1.5 mr-2 flex-shrink-0"></div>
                  <span>Diversificação de receitas em múltiplas categorias</span>
                </li>
              </ul>
            </div>
            
            <div>
              <h4 className="font-semibold text-gray-800 mb-3">⚠️ Atenção</h4>
              <ul className="space-y-2">
                <li className="flex items-start">
                  <div className="w-2 h-2 rounded-full bg-warning-500 mt-1.5 mr-2 flex-shrink-0"></div>
                  <span>Monitorar aumento de despesas operacionais</span>
                </li>
                <li className="flex items-start">
                  <div className="w-2 h-2 rounded-full bg-warning-500 mt-1.5 mr-2 flex-shrink-0"></div>
                  <span>Analisar eficiência por centro de custo</span>
                </li>
                <li className="flex items-start">
                  <div className="w-2 h-2 rounded-full bg-warning-500 mt-1.5 mr-2 flex-shrink-0"></div>
                  <span>Revisar contratos e fornecedores trimestralmente</span>
                </li>
              </ul>
            </div>
          </div>
          
          <div className="mt-6 pt-6 border-t border-gray-200">
            <h4 className="font-semibold text-gray-800 mb-3">🎯 Metas para o Próximo Período</h4>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="p-3 bg-blue-50 rounded-lg">
                <p className="text-sm font-medium text-blue-800">Aumentar Margem</p>
                <p className="text-2xl font-bold text-blue-600">+5%</p>
                <p className="text-xs text-blue-700">Meta de margem líquida</p>
              </div>
              
              <div className="p-3 bg-green-50 rounded-lg">
                <p className="text-sm font-medium text-green-800">Reduzir Despesas</p>
                <p className="text-2xl font-bold text-green-600">-10%</p>
                <p className="text-xs text-green-700">Operacionais não essenciais</p>
              </div>
              
              <div className="p-3 bg-purple-50 rounded-lg">
                <p className="text-sm font-medium text-purple-800">Crescimento</p>
                <p className="text-2xl font-bold text-purple-600">+15%</p>
                <p className="text-xs text-purple-700">Receita total</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Relatorios;
